import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Trophy, Crown, Timer, Loader2, Medal, Share2 } from 'lucide-react';
import { shareResult } from '../lib/shareCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const fmtTime = (ms) => {
  if (ms == null) return '--';
  return `${(ms / 1000).toFixed(2)}s`;
};

export const LeaderboardDialog = ({ open, onOpenChange, timeMs }) => {
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);

  const fetchScores = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/scores`);
      setScores(res.data || []);
    } catch (e) {
      setScores([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSavedId(null);
      setName('');
      fetchScores();
    }
  }, [open, fetchScores]);

  const saveScore = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Enter your operator name to save your time!');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/scores`, { name: name.trim(), time_ms: Math.round(timeMs) });
      setSavedId(res.data.id);
      toast.success('Time saved to the leaderboard!');
      await fetchScores();
    } catch (err) {
      toast.error('Could not save your time. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const canSave = timeMs != null && !savedId;

  const onShare = async () => {
    const res = await shareResult(timeMs);
    if (res.method === 'cancel') return;
    if (res.method === 'fallback') {
      toast.success('Result card saved & caption copied!', { description: 'Share it with your friends!' });
    } else {
      toast.success('Sharing your result!');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px] max-h-[88dvh] overflow-hidden flex flex-col" data-testid="leaderboard-dialog">
        <div className="absolute top-0 left-0 right-0 h-2 hazard-strip rounded-t-lg" />
        <DialogHeader className="pt-2">
          <DialogTitle className="font-display tracking-wide text-2xl flex items-center gap-2">
            <Trophy className="text-[hsl(var(--hazard-yellow))]" size={22} /> Top Operators
          </DialogTitle>
          <DialogDescription>Fastest drive to Cottage 837 wins the hard hat!</DialogDescription>
        </DialogHeader>

        {/* save-your-time card */}
        {canSave && (
          <form onSubmit={saveScore} className="rounded-xl border border-black/10 bg-orange-50 p-3 space-y-2" data-testid="save-score-card">
            <div className="flex items-center gap-2 text-sm font-semibold text-[#1F2328]">
              <Timer size={16} className="text-[hsl(var(--orange))]" />
              Your time: <span className="font-mono-tel">{fmtTime(timeMs)}</span>
            </div>
            <div className="flex gap-2">
              <Input
                data-testid="score-name-input"
                placeholder="Operator name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
                className="h-10"
              />
              <Button
                type="submit"
                disabled={saving}
                data-testid="score-save-button"
                className="chunky-btn bg-[hsl(var(--orange))] hover:bg-[hsl(18_100%_50%)] text-white font-semibold px-4"
              >
                {saving ? <Loader2 className="animate-spin" size={16} /> : 'Save'}
              </Button>
            </div>
          </form>
        )}

        {timeMs != null && (
          <Button
            onClick={onShare}
            data-testid="leaderboard-share-button"
            variant="outline"
            className="w-full gap-2 border-[hsl(var(--orange))] text-[hsl(var(--orange))] hover:bg-orange-50 font-semibold"
          >
            <Share2 size={16} /> Share my result ({fmtTime(timeMs)})
          </Button>
        )}

        {/* list */}
        <div className="mt-1 overflow-y-auto pr-1" style={{ maxHeight: '48dvh' }} data-testid="leaderboard-list">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="animate-spin mr-2" size={18} /> Loading times...
            </div>
          ) : scores.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No times yet — be the first Top Operator!
            </div>
          ) : (
            <ol className="space-y-1.5">
              {scores.map((s, i) => {
                const isMine = s.id === savedId;
                const isTop = i === 0;
                return (
                  <li
                    key={s.id}
                    data-testid={`leaderboard-row-${i}`}
                    className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${
                      isTop
                        ? 'bg-[#1F2328] text-white border-[hsl(var(--hazard-yellow))]'
                        : isMine
                        ? 'bg-orange-50 border-[hsl(var(--orange))]'
                        : 'bg-white border-black/10'
                    }`}
                  >
                    <span className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-display text-sm ${
                      isTop ? 'bg-[hsl(var(--hazard-yellow))] text-[#1F2328]' : i === 1 ? 'bg-gray-200 text-gray-700' : i === 2 ? 'bg-amber-200 text-amber-800' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {i + 1}
                    </span>
                    <span className="flex-1 min-w-0 font-semibold truncate flex items-center gap-1.5">
                      {s.name}
                      {isTop && (
                        <span data-testid="top-operator-badge" className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--hazard-yellow))] text-[#1F2328] text-[10px] font-display tracking-wider px-2 py-0.5">
                          <Crown size={11} /> TOP OPERATOR
                        </span>
                      )}
                      {isMine && !isTop && <span className="text-[10px] text-[hsl(var(--orange))] font-bold">(YOU)</span>}
                    </span>
                    {!isTop && i <= 2 && <Medal size={15} className={i === 1 ? 'text-gray-400' : 'text-amber-600'} />}
                    <span className={`font-mono-tel text-sm font-semibold ${isTop ? 'text-[hsl(var(--hazard-yellow))]' : 'text-[#1F2328]'}`}>
                      {fmtTime(s.time_ms)}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
